import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import * as api from '../services/api';
import { useToast } from './Toast';

interface ReportRecord {
  id: string;
  report_type: string;
  title: string;
  status: string;
  format: string;
  file_size: number;
  generated_at: string;
  created_at: string;
}

interface ClientOption {
  id: string;
  name: string;
}

const REPORT_TYPES: Record<string, { label: string; description: string; icon: React.ElementType; needsClient: boolean }> = {
  posture_report: {
    label: 'Posture Report',
    description: 'Full fleet security posture overview with per-client and per-agent scoring breakdown.',
    icon: DocumentChartBarIcon,
    needsClient: false,
  },
  compliance_report_card: {
    label: 'Compliance Report Card',
    description: 'Monthly compliance report card for a specific client with CIS control family breakdown.',
    icon: ClipboardDocumentListIcon,
    needsClient: true,
  },
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatReportType(type: string): string {
  return REPORT_TYPES[type]?.label || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const ReportsPage: React.FC = () => {
  const { showToast } = useToast();
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<string>('');

  const fetchReports = useCallback(async () => {
    try {
      const data = await api.getReportHistory();
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load report history:', err);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const summary = await api.getComplianceSummary();
      if (summary?.clients) {
        setClients(summary.clients.map((c: any) => ({ id: c.clientId, name: c.clientName })));
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchReports(), fetchClients()]).finally(() => setLoading(false));
  }, [fetchReports, fetchClients]);

  const handleGenerate = async (reportType: string) => {
    const config = REPORT_TYPES[reportType];
    if (config?.needsClient && !selectedClient) {
      showToast('Select a client first', 'error');
      return;
    }

    setGenerating(reportType);
    try {
      await api.generateReport(reportType, config?.needsClient ? selectedClient : undefined);
      showToast(`${config?.label || 'Report'} downloaded`, 'success');
      fetchReports();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Generation failed: ${msg}`, 'error');
    } finally {
      setGenerating(null);
    }
  };

  const handleDownload = async (reportId: string) => {
    setDownloading(reportId);
    try {
      await api.downloadReport(reportId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Download failed: ${msg}`, 'error');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Generate Reports Section */}
      <div>
        <h2 className="text-lg font-medium text-white mb-4">Generate Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(REPORT_TYPES).map(([type, config]) => {
            const Icon = config.icon;
            const isGenerating = generating === type;
            return (
              <div
                key={type}
                className="bg-anchor-card border border-white/[0.06] rounded-xl p-6 flex flex-col"
              >
                <div className="flex items-start space-x-3 mb-3">
                  <div className="p-2 bg-anchor-blue/10 rounded-lg">
                    <Icon className="h-6 w-6 text-anchor-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium">{config.label}</h3>
                    <p className="text-sm text-anchor-muted mt-1">{config.description}</p>
                  </div>
                </div>

                {config.needsClient && (
                  <div className="mt-3 mb-3">
                    <label className="block text-xs text-anchor-muted mb-1">Select Client</label>
                    <select
                      value={selectedClient}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      className="w-full bg-anchor-navy border border-white/[0.1] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-anchor-blue/50"
                    >
                      <option value="">-- Select a client --</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  onClick={() => handleGenerate(type)}
                  disabled={isGenerating || (config.needsClient && !selectedClient)}
                  className="mt-auto flex items-center justify-center px-4 py-2.5 bg-anchor-blue/10 hover:bg-anchor-blue/20 text-anchor-blue rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Generate PDF
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Report History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-white">Report History</h2>
          <button
            onClick={fetchReports}
            className="text-sm text-anchor-muted hover:text-white transition-colors flex items-center"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh
          </button>
        </div>

        {reports.length === 0 ? (
          <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-12 text-center">
            <DocumentChartBarIcon className="h-12 w-12 text-white/20 mx-auto mb-3" />
            <p className="text-anchor-muted">No reports generated yet</p>
            <p className="text-sm text-white/30 mt-1">Generate a report above to get started</p>
          </div>
        ) : (
          <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-4 py-3 text-xs font-medium text-anchor-muted uppercase tracking-wider">Report</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-anchor-muted uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-anchor-muted uppercase tracking-wider">Generated</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-anchor-muted uppercase tracking-wider">Size</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-anchor-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-sm text-white">{report.title}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-anchor-blue/10 text-anchor-blue">
                        {formatReportType(report.report_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-anchor-muted">
                      {report.generated_at
                        ? new Date(report.generated_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                          })
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-anchor-muted">
                      {report.file_size ? formatFileSize(report.file_size) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDownload(report.id)}
                        disabled={downloading === report.id}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-anchor-blue hover:bg-anchor-blue/10 rounded-lg transition-colors disabled:opacity-40"
                      >
                        {downloading === report.id ? (
                          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <ArrowDownTrayIcon className="h-3.5 w-3.5 mr-1" />
                            Download
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
